import React from 'react';
import { ShieldAlertIcon, GlobeIcon } from 'lucide-react';
import { TopoLines } from './TopoLines';
const blocks = [{
  time: 'Portal + customer',
  name: 'Symptom',
  body: 'What the customer or the monitoring portal reports — low, zero, offline, not charging, tripping — with 10-second questions and the likely causes, quickest to rule out first.',
  dot: 'bg-sky'
}, {
  time: 'Inverter · MLPE · BMS',
  name: 'Code',
  body: 'The code family that usually comes with the fault and what it tends to mean — always with “verify in the current manual and firmware.”',
  dot: 'bg-vol-3'
}, {
  time: 'Array · inverter · grid · battery',
  name: 'Split',
  body: 'Where to split the system to locate the zone of the fault, and which result sends you to which half.',
  dot: 'bg-vol-4'
}, {
  time: 'Meter · clamp · megger · IV · thermal',
  name: 'Prove',
  body: 'The measurement that confirms it, in safe order: what to measure, where, with which instrument and what reading to expect.',
  dot: 'bg-ember'
}, {
  time: 'Report · estimate · claim',
  name: 'Fix & File',
  body: 'The repair or replacement, the report line, the data for the warranty claim and the repair-replace-upgrade call for that fault.',
  dot: 'bg-moss'
}];
export function ProductReveal() {
  return <section aria-labelledby="mechanism-heading" className="topo-dark relative w-full overflow-hidden border-y-4 border-ember bg-forest" data-id="element-288" data-mp="1orklkm1pll39">
      <TopoLines className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 text-paper/[0.06]" data-id="element-289" data-mp="w35b8ctivqy9" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-290" data-mp="112i4bfazlu9a">
        <div className="text-center" data-id="element-291" data-mp="161uottedyp8p">
          <p className="eyebrow text-ember" data-id="element-292" data-mp="162w6whttkbzx">The mechanism</p>
          <h2 id="mechanism-heading" className="trail-underline mt-3 font-display text-[34px] leading-tight text-paper sm:text-6xl" data-id="element-293" data-mp="2ci6tmdbpr5d9">
            The Split-and-Prove Path
          </h2>
          <p className="field-note mt-5 text-[28px] leading-tight text-paper sm:text-4xl" data-id="element-294" data-mp="1rs2wrzn39maf">
            Split the system. Prove the fault. Then fix it.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-base text-paper/80 sm:text-lg" data-id="element-295" data-mp="1mw5cxjcm9xsw">
            Every one of the 159 cards runs on the same five checkpoints, in the
            same order, so after a few calls you work it from memory. One rule
            behind all of them: you don&apos;t replace anything until you&apos;ve
            split the system and a measurement has proved the fault.
          </p>
        </div>

        {/* Recorrido de 5 checkpoints */}
        <ol className="relative mx-auto mt-9 max-w-2xl" data-id="element-296" data-mp="1itqv92kop437">
          <span aria-hidden="true" className="absolute bottom-6 left-[19px] top-6 border-l-2 border-dashed border-paper/25" data-id="element-297" data-mp="1e0xt8mglo9ut" />
          {blocks.map((block, i) => <li key={block.name} className="relative flex gap-4 pb-4 last:pb-0" data-id="element-298" data-mp="ldbr7in0tda2" data-mpi={i}>
              <span className={`relative z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full font-mono text-sm font-bold text-pine ring-4 ring-forest ${block.dot}`} data-id="element-299" data-mp="2x05hx1avnqtj">
                {i + 1}
              </span>
              <div className="flex-1 rounded-xl border border-paper/10 bg-paper/[0.06] px-4 py-3" data-id="element-300" data-mp="2n3k882nwxrjd">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3" data-id="element-301" data-mp="2s1c1p147hlpo">
                  <span className="font-display text-xl uppercase tracking-wide text-paper" data-id="element-302" data-mp="3fr6ddfcsh48c">
                    {block.name}
                  </span>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-ember" data-id="element-303" data-mp="36q8ipno8q92f">
                    {block.time}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-snug text-paper/80" data-id="element-304" data-mp="3atensrzm9u7b">
                  {block.body}
                </p>
              </div>
            </li>)}
        </ol>

        <div className="mx-auto mt-6 grid max-w-2xl gap-3 sm:grid-cols-2" data-id="element-305" data-mp="1zaahgllnozza">
          <div className="rounded-xl bg-ember-soft px-4 py-4 text-pine" data-id="element-306" data-mp="2fkb7q6u3mjcs">
            <span className="flex items-center gap-2 font-display text-lg" data-id="element-307" data-mp="2asjgrv716bm4">
              <ShieldAlertIcon className="h-5 w-5 text-signal" aria-hidden="true" data-id="element-308" data-mp="25tgo7a88lxv1" />
              The Safety Block
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-309" data-mp="20vojffmhuh9f">
              Every card opens with its zone&apos;s shutdown order, PPE and
              verify-absence-of-voltage step — because the array never sleeps in
              daylight.
            </p>
          </div>
          <div className="rounded-xl bg-sky-soft px-4 py-4 text-pine" data-id="element-310" data-mp="2ojoir3xvffzm">
            <span className="flex items-center gap-2 font-display text-lg" data-id="element-311" data-mp="2kimlgsnile0a">
              <GlobeIcon className="h-5 w-5 text-forest" aria-hidden="true" data-id="element-312" data-mp="2kkp8d3tder0s" />
              The Code Column Method
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-313" data-mp="193pg2870wc6r">
              One check, four countries: NEC 690/705/706, CSA C22.1 Section 64,
              BS 7671 Section 712 and AS/NZS 5033, 5139 and 4777, cited by
              number.
            </p>
          </div>
        </div>
      </div>
    </section>;
}